// ============================================================================
// AVATAR GROUP BUILDER COMPONENT
// Create and edit avatar groups with slot-based avatar assignment
// ============================================================================

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save, Plus, Trash2, Search, AlertCircle, CheckCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { AvatarDefinition, AvatarCategory, AvatarScope } from '../types';
import {
  browseAvatars,
  getAvatarGroup,
  createAvatarGroup,
  updateAvatarGroup
} from '../services/avatarCatalogService';

interface AvatarGroupBuilderProps {
  mode: 'create' | 'edit';
}

interface SlotAssignment {
  slotNumber: number;
  avatarDefinitionId?: string;
  versionNumber?: number;
  avatarName?: string;
  avatarCategory?: AvatarCategory;
}

export default function AvatarGroupBuilder({ mode }: AvatarGroupBuilderProps) {
  const navigate = useNavigate();
  const { groupId } = useParams<{ groupId: string }>();
  const { user } = useAuth();

  // Form state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [slots, setSlots] = useState<SlotAssignment[]>([
    { slotNumber: 1 },
    { slotNumber: 2 },
    { slotNumber: 3 },
    { slotNumber: 4 },
    { slotNumber: 5 }
  ]);

  // Avatar catalog state
  const [availableAvatars, setAvailableAvatars] = useState<AvatarDefinition[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<AvatarCategory | ''>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const organizationId = user?.organizationId || '';

  const loadGroup = useCallback(async () => {
    try {
      setLoading(true);
      const result = await getAvatarGroup(groupId!);
      const group = result.group;
      setName(group.name);
      setDescription(group.description);

      // Convert avatar slots to slot assignments
      const loadedSlots: SlotAssignment[] = group.avatarSlots.map(slot => ({
        slotNumber: slot.slotNumber,
        avatarDefinitionId: slot.avatarDefinitionId,
        versionNumber: slot.versionNumber
      }));

      // Fill in empty slots up to 10
      for (let i = 1; i <= 10; i++) {
        if (!loadedSlots.find(s => s.slotNumber === i)) {
          loadedSlots.push({ slotNumber: i });
        }
      }

      setSlots([...loadedSlots].sort((a, b) => a.slotNumber - b.slotNumber));
    } catch (err: any) {
      setError(err.message || 'Failed to load avatar group');
    } finally {
      setLoading(false);
    }
  }, [groupId]);

  // Load existing group for edit mode
  useEffect(() => {
    if (mode === 'edit' && groupId) {
      loadGroup();
    }
    loadAvailableAvatars();
  }, [mode, groupId, loadGroup]);

  const loadAvailableAvatars = async () => {
    try {
      const result = await browseAvatars({
        scope: AvatarScope.ORGANIZATION // Only show org-level avatars
      });
      setAvailableAvatars(result.avatars);
    } catch (err: any) {
      setError(err.message || 'Failed to load available avatars');
    }
  };

  const handleAddSlot = () => {
    const nextSlotNumber = Math.max(...slots.map(s => s.slotNumber)) + 1;
    setSlots([...slots, { slotNumber: nextSlotNumber }]);
  };

  const handleRemoveSlot = (slotNumber: number) => {
    if (slots.length <= 1) {
      alert('You must have at least one slot');
      return;
    }
    setSlots(slots.filter(s => s.slotNumber !== slotNumber));
  };

  const handleAssignAvatar = (slotNumber: number, avatar: AvatarDefinition) => {
    setSlots(slots.map(slot =>
      slot.slotNumber === slotNumber
        ? {
            slotNumber,
            avatarDefinitionId: avatar.avatarDefinitionId,
            versionNumber: avatar.versionNumber,
            avatarName: avatar.name,
            avatarCategory: avatar.category
          }
        : slot
    ));
  };

  const handleClearSlot = (slotNumber: number) => {
    setSlots(slots.map(slot =>
      slot.slotNumber === slotNumber
        ? { slotNumber }
        : slot
    ));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (!name.trim()) {
      alert('Please enter a group name');
      return;
    }
    if (!description.trim()) {
      alert('Please enter a description');
      return;
    }

    const assignedSlots = slots.filter(s => s.avatarDefinitionId);
    if (assignedSlots.length === 0) {
      alert('Please assign at least one avatar to a slot');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const avatarSlots = assignedSlots.map(slot => ({
        avatarDefinitionId: slot.avatarDefinitionId!,
        versionNumber: slot.versionNumber!,
        slotNumber: slot.slotNumber
      }));

      if (mode === 'create') {
        await createAvatarGroup(
          name.trim(),
          description.trim(),
          organizationId,
          avatarSlots,
          user?.userId || ''
        );
        alert('Avatar group created successfully!');
      } else {
        await updateAvatarGroup(groupId!, {
          name: name.trim(),
          description: description.trim(),
          avatarSlots
        });
        alert('Avatar group updated successfully!');
      }

      navigate('/avatars/groups');
    } catch (err: any) {
      setError(err.message || 'Failed to save avatar group');
      alert(`Error: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  // Filter available avatars
  const filteredAvatars = availableAvatars.filter(avatar => {
    const matchesSearch = !searchTerm ||
      avatar.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      avatar.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      avatar.keywords.some(k => k.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesCategory = !selectedCategory || avatar.category === selectedCategory;

    return matchesSearch && matchesCategory;
  });

  // Get assigned avatar IDs for highlighting
  const assignedAvatarIds = new Set(
    slots
      .filter(s => s.avatarDefinitionId)
      .map(s => s.avatarDefinitionId)
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center py-20 text-text-sub">
        <div className="text-sm font-medium">Loading...</div>
      </div>
    );
  }

  let submitButtonLabel: string;
  if (saving) submitButtonLabel = 'Saving...';
  else if (mode === 'create') submitButtonLabel = 'Create Group';
  else submitButtonLabel = 'Update Group';

  return (
    <div className="p-8 max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={() => navigate('/avatars/groups')}
          className="flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-text-main bg-transparent border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors mb-5"
        >
          <ArrowLeft size={14} />
          Back to Groups
        </button>

        <h2 className="text-3xl font-bold text-text-main">
          {mode === 'create' ? 'Create Avatar Group' : 'Edit Avatar Group'}
        </h2>
        <p className="text-text-sub text-sm mt-1">
          {mode === 'create'
            ? 'Build a reusable collection of avatars for your workshops'
            : 'Update your avatar group configuration'}
        </p>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6 flex items-center gap-3">
          <AlertCircle size={18} className="text-red-600 flex-shrink-0" />
          <p className="text-sm text-red-700"><strong>Error:</strong> {error}</p>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column: Group Details */}
          <div className="lg:col-span-1">
            <div className="bg-white border border-slate-200 rounded-2xl p-6">
              <h3 className="text-lg font-semibold text-text-main mb-5">Group Details</h3>

              {/* Name */}
              <div className="mb-5">
                <label className="block text-sm font-semibold text-text-main mb-2">Group Name *</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g., Healthcare Facilitators"
                  required
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 text-sm text-text-main focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all placeholder:text-slate-400"
                />
              </div>

              {/* Description */}
              <div className="mb-5">
                <label className="block text-sm font-semibold text-text-main mb-2">Description *</label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Describe this avatar group and when to use it..."
                  required
                  rows={4}
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 text-sm text-text-main focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all placeholder:text-slate-400 resize-y"
                />
              </div>

              {/* Slot Summary */}
              <div className="bg-slate-50 rounded-xl p-4 mt-6">
                <h4 className="text-xs font-semibold text-text-main mb-3 uppercase tracking-wider">Summary</h4>
                <div className="flex justify-between mb-2">
                  <span className="text-xs text-text-sub">Total Slots:</span>
                  <span className="text-sm font-semibold text-text-main">{slots.length}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-xs text-text-sub">Assigned:</span>
                  <span className="text-sm font-semibold text-green-600">
                    {slots.filter(s => s.avatarDefinitionId).length}
                  </span>
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={saving}
              className="w-full mt-5 flex items-center justify-center gap-2 px-5 py-3.5 bg-primary hover:bg-primary/90 text-text-main font-bold rounded-xl transition-all shadow-md shadow-primary/20 text-sm disabled:bg-slate-200 disabled:text-slate-400 disabled:cursor-not-allowed disabled:shadow-none"
            >
              <Save size={18} />
              {submitButtonLabel}
            </button>
          </div>

          {/* Right Column: Slot Assignment */}
          <div className="lg:col-span-2 space-y-5">
            {/* Slots Section */}
            <div className="bg-white border border-slate-200 rounded-2xl p-6">
              <div className="flex justify-between items-center mb-5">
                <h3 className="text-lg font-semibold text-text-main">Avatar Slots</h3>
                <button
                  type="button"
                  onClick={handleAddSlot}
                  className="flex items-center gap-1.5 px-3 py-2 bg-blue-50 border border-blue-200 rounded-xl text-xs font-semibold text-blue-700 hover:bg-blue-100 transition-colors"
                >
                  <Plus size={14} />
                  Add Slot
                </button>
              </div>

              <div className="space-y-3">
                {slots.map((slot) => (
                  <div
                    key={slot.slotNumber}
                    className={`p-4 rounded-xl border-2 flex items-center gap-3 transition-colors ${
                      slot.avatarDefinitionId
                        ? 'bg-green-50 border-green-200'
                        : 'bg-slate-50 border-slate-200'
                    }`}
                  >
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center text-sm font-bold text-white flex-shrink-0 ${
                      slot.avatarDefinitionId ? 'bg-green-500' : 'bg-slate-300'
                    }`}>
                      {slot.slotNumber}
                    </div>

                    <div className="flex-1 min-w-0">
                      {slot.avatarDefinitionId ? (
                        <>
                          <div className="text-sm font-semibold text-text-main">{slot.avatarName}</div>
                          <div className="text-[11px] text-text-sub">{slot.avatarCategory}</div>
                        </>
                      ) : (
                        <div className="text-xs text-slate-400">No avatar assigned</div>
                      )}
                    </div>

                    <div className="flex gap-2">
                      {slot.avatarDefinitionId && (
                        <button
                          type="button"
                          onClick={() => handleClearSlot(slot.slotNumber)}
                          className="px-2.5 py-1.5 bg-red-50 border border-red-200 rounded-lg text-[11px] font-semibold text-red-600 hover:bg-red-100 transition-colors"
                        >
                          Clear
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => handleRemoveSlot(slot.slotNumber)}
                        disabled={slots.length <= 1}
                        className="p-1.5 border border-slate-200 rounded-lg text-text-sub hover:text-red-500 hover:border-red-200 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Avatar Catalog Section */}
            <div className="bg-white border border-slate-200 rounded-2xl p-6">
              <h3 className="text-lg font-semibold text-text-main mb-5">Available Avatars</h3>

              {/* Search and Filters */}
              <div className="mb-5 space-y-3">
                <div className="relative">
                  <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search avatars..."
                    className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-slate-200 text-sm text-text-main focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all placeholder:text-slate-400"
                  />
                </div>

                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value as AvatarCategory | '')}
                  className="w-full px-4 py-2.5 rounded-xl border border-slate-200 text-sm text-text-main focus:ring-2 focus:ring-primary focus:border-transparent outline-none cursor-pointer bg-white"
                >
                  <option value="">All Categories</option>
                  {Object.values(AvatarCategory).map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>

              {/* Avatar Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 max-h-[500px] overflow-y-auto p-1">
                {filteredAvatars.map((avatar) => {
                  const isAssigned = assignedAvatarIds.has(avatar.avatarDefinitionId);

                  return (
                    <div
                      key={avatar.avatarDefinitionId}
                      onClick={() => {
                        const emptySlot = slots.find(s => !s.avatarDefinitionId);
                        if (emptySlot) {
                          handleAssignAvatar(emptySlot.slotNumber, avatar);
                        } else {
                          alert('All slots are filled. Remove an avatar or add more slots.');
                        }
                      }}
                      className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                        isAssigned
                          ? 'bg-green-50 border-green-200'
                          : 'bg-white border-slate-200 hover:border-primary hover:-translate-y-0.5'
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <h4 className="text-xs font-semibold text-text-main flex-1 min-w-0 truncate">
                          {avatar.name}
                        </h4>
                        {isAssigned && <CheckCircle size={14} className="text-green-500 flex-shrink-0" />}
                      </div>
                      <p className="text-[11px] text-text-sub line-clamp-2 leading-relaxed mb-2">
                        {avatar.description}
                      </p>
                      <span className="inline-block px-2 py-0.5 bg-slate-100 rounded text-[10px] font-semibold text-text-sub">
                        {avatar.category}
                      </span>
                    </div>
                  );
                })}
              </div>

              {filteredAvatars.length === 0 && (
                <div className="py-10 text-center text-sm text-slate-400">
                  No avatars found matching your criteria
                </div>
              )}
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}
